import { initialSessionState, SessionContext, sessionReducer } from '@/entities/session';
import { ReactNode, useMemo, useReducer, useRef } from 'react';
import { useSessionApiBinding } from "./hooks/useSessionApiBinding";
import { useSessionCrossTabSync } from "./hooks/useSessionCrossTabSync";
import { useSessionIdleLogout } from "./hooks/useSessionIdleLogout";
import { useSessionOrganizationState } from "./hooks/useSessionOrganizationState";
import { useSessionAuthActions } from "./hooks/useSessionAuthActions";
import { useSessionBootstrap } from "./hooks/useSessionBootstrap";

type SessionProviderProps = {
    children: ReactNode;
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
    const tokenRef = useRef<string | null>(null);
    const [state, dispatch] = useReducer(sessionReducer, initialSessionState);
    const { login, logout, refreshToken } = useSessionAuthActions(dispatch, tokenRef);
    const { userOrganizations, currentOrganization, switchOrganization } = useSessionOrganizationState(state, dispatch);

    useSessionBootstrap(dispatch, tokenRef);
    useSessionApiBinding(dispatch, tokenRef, refreshToken)
    useSessionCrossTabSync(dispatch, tokenRef);
    useSessionIdleLogout(state.user, logout);

    const contextValue = useMemo(
        () => ({
            status: state.status,
            isAuthenticated: state.user !== null,
            user: state.user,
            login,
            logout,
            userOrganizations,
            currentOrganization,
            switchOrganization,
        }),
        [
            state.status,
            state.user,
            login,
            logout,
            userOrganizations,
            currentOrganization,
            switchOrganization,
        ]
    );

    return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
};
